import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { KeycloakClient, PaginatedResponse } from '../../../core/models/client.model';
import { ClientService } from '../../../core/services/client.service';

@Component({
  selector: 'app-client-list',
  imports: [
    CommonModule,
    RouterModule,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatChipsModule,
    MatTooltipModule,
    MatCardModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    MatMenuModule,
    MatBadgeModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    // MatDivider et AppLoaderComponent supprimés
  ],
  templateUrl: './client-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./client-list.component.scss']
})
export class ClientListComponent implements OnInit, OnDestroy {
  // ... propriétés existantes (clients, totalClients, pageSize, currentPage, loading, selectedFilter, allClients, totalAllClients)
  clients: KeycloakClient[] = [];
  totalClients = 0;
  pageSize = 10;
  currentPage = 0;
  loading = false;
  selectedFilter = 'all';
  allClients: KeycloakClient[] = [];
  totalAllClients = 0;

  private clientsBeingToggled = new Set<string>();
  searchControl = new FormControl('');
  private destroy$ = new Subject<void>();

  tabs = [
    { label: 'Tous', filter: 'all', badgeClass: 'success', count: 0 },
    { label: 'Privés', filter: 'confidential', badgeClass: 'info', count: 0 },
    // { label: 'Administrations', filter: 'bearer', badgeClass: 'purple', count: 0 },
    { label: 'Publiques', filter: 'public', badgeClass: 'danger', count: 0 }
  ];
  selectedTabIndex = 0;
  activeDropdown: string | null = null;
  favoriteIds = new Set<string>();

  constructor(
    private clientService: ClientService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.setupSearch();
    this.loadClientsFromAPI();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredClients(): KeycloakClient[] {
    const filter = this.tabs[this.selectedTabIndex].filter;
    let filtered = [...this.allClients];

    const search = this.searchControl.value?.trim().toLowerCase() || '';
    if (search) {
      filtered = filtered.filter(client =>
        client.clientId?.toLowerCase().includes(search) ||
        client.name?.toLowerCase().includes(search) ||
        client.description?.toLowerCase().includes(search)
      );
    }

    switch (filter) {
      case 'public':
        filtered = filtered.filter(c => c.publicClient && !c.bearerOnly);
        break;
      case 'confidential':
        filtered = filtered.filter(c => !c.publicClient && !c.bearerOnly);
        break;
      case 'bearer':
        filtered = filtered.filter(c => c.bearerOnly);
        break;
      default:
        break;
    }
    return filtered;
  }

  updateTabCounts() {
    const all = this.allClients;

    this.tabs[0].count = all.length;
    this.tabs[1].count = all.filter(c => !c.publicClient && !c.bearerOnly).length;
    this.tabs[2].count = all.filter(c => c.publicClient && !c.bearerOnly).length;
  }

  selectTab(index: number) {
    this.selectedTabIndex = index;
    this.currentPage = 0;
  }

  toggleFavorite(client: KeycloakClient) {
    if (this.favoriteIds.has(client.id)) {
      this.favoriteIds.delete(client.id);
    } else {
      this.favoriteIds.add(client.id);
    }
  }

  toggleDropdown(clientId: string) {
    this.activeDropdown = this.activeDropdown === clientId ? null : clientId;
  }

  setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage = 0;
      });
  }

  // private loadClientsFromAPI(): void {
  //   this.loading = true;
  //   const search = this.searchControl.value?.trim() || '';

  //   this.clientService.getAllClients(0, 1000, search)
  //     .subscribe({
  //       next: (response: PaginatedResponse<KeycloakClient>) => {
  //         this.allClients = response.content || [];
  //         this.totalAllClients = response.totalElements || 0;
  //         this.updateTabCounts();
  //         this.loading = false;
  //       },
  //       error: (error) => {
  //         console.error('Erreur lors du chargement des clients:', error);
  //         this.showErrorMessage(`Erreur lors du chargement: ${error.message}`);
  //         this.allClients = [];
  //         this.totalAllClients = 0;
  //         this.loading = false;
  //       }
  //     });
  // }

  private loadClientsFromAPI(): void {
    this.loading = true;
    // La recherche est gérée côté frontend (car l'endpoint ne la supporte pas)
    // On peut filtrer après réception.
    this.clientService.getApplicationClients(['external-app', 'internal-app'])
      .subscribe({
        next: (clients: KeycloakClient[]) => {
          // Si besoin d'une recherche côté client
          const search = this.searchControl.value?.trim().toLowerCase() || '';
          let filtered = clients;
          if (search) {
            filtered = clients.filter(c =>
              c.clientId?.toLowerCase().includes(search) ||
              c.name?.toLowerCase().includes(search) ||
              c.description?.toLowerCase().includes(search)
            );
          }
          this.allClients = filtered;
          this.totalAllClients = filtered.length;
          this.updateTabCounts();
          this.loading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement des applications :', error);
          this.showErrorMessage(`Erreur : ${error.message}`);
          this.allClients = [];
          this.totalAllClients = 0;
          this.loading = false;
        }
      });
  }

  getClientTypeLabel(client: KeycloakClient): string {
    if (client.bearerOnly) return 'Bearer';
    if (client.publicClient) return 'Public';
    return 'Privé';
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  addClient(): void {
    this.router.navigate(['/dashboard/clients/add']);
  }

  navigateToEditClient(clientId: string): void {
    this.router.navigate(['/dashboard/clients/edit', clientId]);
  }

  openDeleteDialog(client: KeycloakClient): void {
    const confirmDialog = confirm(
      `Êtes-vous sûr de vouloir supprimer le client "${client.name || client.clientId}" ?\n\n` +
      `Cette action est irréversible et supprimera définitivement ce client.`
    );
    if (confirmDialog) {
      this.executeDeleteClient(client);
    }
  }

  private executeDeleteClient(client: KeycloakClient): void {
    this.loading = true;
    this.clientService.deleteClient(client.id)
      .subscribe({
        next: () => {
          this.showSuccessMessage(`Client "${client.name || client.clientId}" supprimé avec succès`);
          this.loadClientsFromAPI();
        },
        error: (error) => {
          console.error('Erreur lors de la suppression:', error);
          this.showErrorMessage(`Erreur lors de la suppression: ${error.message}`);
          this.loading = false;
        }
      });
  }

  // Modification : accepte un Event et extrait la valeur checked
  toggleClientStatus(client: KeycloakClient, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newChecked = input.checked;

    // On restaure l'état visuel immédiatement (si on veut annuler après confirm)
    input.checked = client.enabled;

    if (this.clientsBeingToggled.has(client.id)) {
      return;
    }

    const newStatus = !client.enabled;
    const statusText = newStatus ? 'activer' : 'désactiver';
    const confirmed = confirm(
      `Êtes-vous sûr de vouloir ${statusText} le client "${client.name || client.clientId}" ?`
    );
    if (confirmed) {
      this.executeToggleClientStatus(client, newStatus);
    }
  }

  private executeToggleClientStatus(client: KeycloakClient, newStatus: boolean): void {
    this.clientsBeingToggled.add(client.id);
    const originalStatus = client.enabled;
    client.enabled = newStatus;

    const updatedClient: Partial<KeycloakClient> = { ...client, enabled: newStatus };
    this.clientService.updateClient(client.id, updatedClient)
      .subscribe({
        next: (updated: KeycloakClient) => {
          const clientIndex = this.clients.findIndex(c => c.id === client.id);
          if (clientIndex !== -1) {
            this.clients[clientIndex] = updated;
          }
          this.showSuccessMessage(
            `Client "${updated.name || updated.clientId}" ${updated.enabled ? 'activé' : 'désactivé'} avec succès`
          );
          this.clientsBeingToggled.delete(client.id);
          this.loadClientsFromAPI(); // pour rafraîchir la liste complète
        },
        error: (error) => {
          console.error('Erreur lors de la modification du statut:', error);
          client.enabled = originalStatus;
          this.clientsBeingToggled.delete(client.id);
          this.showErrorMessage(`Erreur lors de la modification: ${error.message}`);
        }
      });
  }

  regenerateSecret(client: KeycloakClient): void {
    if (client.publicClient) {
      this.showErrorMessage('Les clients publics n\'ont pas de secret');
      return;
    }
    const confirmed = confirm(
      `Êtes-vous sûr de vouloir régénérer le secret du client "${client.name || client.clientId}" ?\n\n` +
      `L'ancien secret ne fonctionnera plus.`
    );
    if (confirmed) {
      this.clientService.regenerateClientSecret(client.id)
        .subscribe({
          next: (response) => {
            navigator.clipboard.writeText(response.secret).then(() => {
              this.showSuccessMessage(
                `Nouveau secret généré et copié dans le presse-papier pour "${client.name || client.clientId}"`
              );
            }).catch(() => {
              this.showSuccessMessage(
                `Nouveau secret généré: ${response.secret.substring(0, 10)}...`
              );
            });
          },
          error: (error) => {
            console.error('Erreur lors de la régénération du secret:', error);
            this.showErrorMessage(`Erreur lors de la régénération: ${error.message}`);
          }
        });
    }
  }

  isClientBeingToggled(clientId: string): boolean {
    return this.clientsBeingToggled.has(clientId);
  }

  getToggleTooltip(client: KeycloakClient): string {
    if (this.isClientBeingToggled(client.id)) {
      return 'Modification en cours...';
    }
    return client.enabled ? 'Cliquez pour désactiver' : 'Cliquez pour activer';
  }

  // Méthodes conservées pour compatibilité (si appelées depuis le template)
  getInitials(clientId: string, name?: string): string {
    const displayName = name || clientId;
    return displayName.substring(0, 2).toUpperCase();
  }

  getAvatarColor(clientId: string): string {
    const colors = ['#45B56F', '#f093fb', '#45B56F', '#43e97b', '#fa709a', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57'];
    let hash = 0;
    for (let i = 0; i < clientId.length; i++) {
      hash = clientId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  getActiveClientsCount(): number {
    return this.clients.filter(client => client.enabled).length;
  }

  getPublicClientsCount(): number {
    return this.clients.filter(client => client.publicClient).length;
  }

  getConfidentialClientsCount(): number {
    return this.clients.filter(client => !client.publicClient).length;
  }

  setFilter(filter: string): void {
    this.selectedFilter = filter;
    this.currentPage = 0;
    // On pourrait appliquer un filtre supplémentaire ici, mais avec les tabs c'est redondant
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.currentPage = 0;
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 4000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 6000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  // Ces méthodes sont conservées pour éviter des erreurs si elles sont utilisées dans le template (elles ne le sont pas)
  getClientType(client: KeycloakClient): string {
    if (client.bearerOnly) return 'Bearer Only';
    if (client.publicClient) return 'Public';
    return 'Confidentiel';
  }

  getClientTypeColor(client: KeycloakClient): string {
    if (client.bearerOnly) return '#9c27b0';
    if (client.publicClient) return '#45B56F';
    return '#4caf50';
  }
}